/**
 * 帮助中心内容语言
 *
 * 这里的"语言"是**内容维度**，只决定目录名 / 文章标题 / 正文取哪一份，
 * 同时驱动界面文案 i18n：界面语言与内容语言共用这份语言列表与同一个存储 key（见 @src/i18n）。
 */
import { ELanguage } from 'odc-shared/i18n';

/** 基准语言：内容缺失时的兜底来源 */
export const BASE_LANGUAGE = ELanguage.En;

/**
 * 帮助中心已启用语言，第一项必须是基准语言。
 *
 * 必须与后端 `public/lang/language.go` 的 `supportCenterLanguages` 保持一致：
 * 后端对未启用的语言会直接报错（运营端）或回退基准语言（展示端），
 * 这里多列一个语言只会让用户切到一个看不到内容的语言。
 * 上线中文时两处一起加 `ELanguage.Zh`。
 *
 * ⚠️ 这个常量是**外展示开关**：临时打开 / 关闭中文，改的就是这一行 ——
 *   打开：`[ELanguage.En, ELanguage.Zh]`   关闭：`[ELanguage.En]`
 * 关掉后语言下拉自动消失（isSingleLanguage() 守卫），后端对 `language=zh` 也只是
 * 静默回退英文，不会报错。
 */
export const SUPPORT_CENTER_LANGUAGES: ELanguage[] = [ELanguage.En, ELanguage.Zh];

/**
 * 语言展示名（切换器文案）。
 *
 * 语言名属于"自我标识"，不随界面语言翻译：English 永远是 English、中文永远是中文（按设计稿）。
 */
export const LANGUAGE_LABELS: Record<string, string> = {
  [ELanguage.En]: 'English',
  [ELanguage.Zh]: '中文',
};

/** 取语言展示名，未登记的语言直接显示语言码 */
export function getLanguageLabel(language?: string): string {
  if (!language) return '';
  return LANGUAGE_LABELS[language] ?? language;
}

/** 语言是否已启用 */
export function isLanguageEnabled(language?: string): language is ELanguage {
  return !!language && SUPPORT_CENTER_LANGUAGES.includes(language as ELanguage);
}

/**
 * 归一化内容语言：未启用的语言一律回落基准语言
 *
 * 展示端不能因为 URL 上带了个陌生 language 就报错 —— 老链接、下线某语言后已发出的
 * 分享链接都会走到这里，静默回退英文是唯一合理行为。
 */
export function normalizeLanguage(language?: string | null): ELanguage {
  return isLanguageEnabled(language ?? undefined) ? (language as ELanguage) : BASE_LANGUAGE;
}

/** 是否只启用了一种语言（此时不展示语言下拉） */
export function isSingleLanguage(): boolean {
  return SUPPORT_CENTER_LANGUAGES.length <= 1;
}

/**
 * 拼接链接上的语言参数，形如 `&language=zh`
 *
 * 基准语言也显式带上（`&language=en`）：链接自带语言，接收方打开就是同一种语言，
 * 不会被对方本地偏好改掉（"缺省=英文"只用于改造前的老链接兜底）。
 */
export function buildLanguageQuery(language?: string): string {
  return `&language=${normalizeLanguage(language)}`;
}
